import { z } from 'zod';
import { RarityKey } from './rarity.js';

/**
 * A REWARD is a mint policy: which collectibles, at what rarity, how many.
 *
 * The single most important property of this shape is that there is exactly
 * ONE supply axis. stefancoat runs six live mechanisms with no arbiter —
 * `quantity`, `assetPoolQuantity`, `assetPrintRuns`, per-tier `tierCounts`,
 * `Asset.uniqueMinted`, `Bundle.quantity` — each with its own counter and its
 * own edition ledger, and `quantity` must equal `assets × assetPoolQuantity`
 * *by convention only*. goodgamegizmo has four and needed a whole module,
 * `services/printRun.ts`, solely to decide which one wins.
 *
 * Here, choosing the axis is part of creating the reward, and there is no
 * resolution module because there is nothing to resolve.
 */

/**
 * The ceiling on one (item, tier) print run.
 *
 * Not arbitrary: a limited run materialises its unclaimed edition numbers as
 * an array on one supply counter, because editions are a claimed SET and not
 * a count (L7). That array has to fit in a Firestore document, and it is read
 * and rewritten inside every mint transaction. Past a few thousand entries
 * the allocator wants a different structure, so it is refused at authoring
 * time rather than discovered at the first mint.
 *
 * The `total` axis gets a higher ceiling because it is ONE counter for the
 * whole reward, where the per-item axes hold one each and multiply by the
 * pool.
 */
export const MAX_PRINT_RUN = 10_000;
export const MAX_TOTAL_PRINT_RUN = 100_000;

/** Where the copies come from. */
export const RewardPool = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('items'),
    item_ids: z.array(z.string()).min(1).max(500),
  }),
  z.object({
    /**
     * Every active item carrying this tag, resolved AT MINT.
     *
     * A live query, and safe to be one because no counter is ever derived
     * from pool size — adding an item adds capacity, removing one strands
     * nothing. stefancoat's pool drift came entirely from a reward-level cap
     * that had to match the pool by convention.
     */
    mode: z.literal('tag'),
    tag: z.string().min(1).max(40),
  }),
]);

/**
 * How a copy gets its tier.
 *
 * The tier is always STAMPED ON THE COPY, like the edition number. What
 * differs is where it comes from. The archetype never carries an
 * authoritative rarity (L50) — one piece of art can be Common in one reward
 * and Epic in another.
 */
export const RewardRarity = z.discriminatedUnion('mode', [
  /** Copies carry no tier at all. */
  z.object({ mode: z.literal('none') }),
  z.object({
    /**
     * Rolled per copy — the trading-card pattern, where any item can appear
     * at any tier.
     *
     * `weights` applies ONLY when supply is not per-tier. With a per-tier
     * print run the sheet IS the distribution: printing 100 commons and 5
     * legendaries already says the odds, and a second set of weights beside
     * it would be two answers to one question.
     */
    mode: z.literal('rolled'),
    weights: z.record(RarityKey, z.number().min(0)).default({}),
  }),
  z.object({
    /** Fixed per item — the game-catalogue pattern. The Legendary Sword is legendary. */
    mode: z.literal('per_item'),
    assign: z.record(z.string(), RarityKey).default({}),
  }),
]);

/**
 * THE ONE AXIS.
 *
 * `null` and `0` are not both expressible here on purpose: "unlimited" is its
 * own variant rather than a magic value, so the L8 confusion — uncapped and
 * never-printed one keystroke apart — cannot be typed.
 */
export const RewardSupply = z.discriminatedUnion('axis', [
  /** Never runs out. Copies carry no edition number. */
  z.object({ axis: z.literal('unlimited') }),
  /** N copies of this reward, across the whole pool. */
  z.object({ axis: z.literal('total'), limit: z.number().int().min(1).max(MAX_TOTAL_PRINT_RUN) }),
  /** N copies of EACH item in the pool. */
  z.object({ axis: z.literal('per_item'), limit: z.number().int().min(1).max(MAX_PRINT_RUN) }),
  z.object({
    /**
     * N copies of each item AT EACH TIER — a print sheet.
     *
     * Their editor's own warning, worth repeating wherever this is rendered:
     * *"'Common 100' across a 14-card pool means 1,400 commons, not 100 —
     * getting that backwards is a two-orders-of-magnitude mistake in how much
     * of a season exists."*
     */
    axis: z.literal('per_item_tier'),
    limits: z.record(RarityKey, z.number().int().min(0).max(MAX_PRINT_RUN)),
  }),
]);

/**
 * How edition numbers are handed out.
 *
 * Random by default: numbering that is not sequential means a collector
 * cannot infer how early they were from their number, and #17 of 25 can be
 * the first ever minted.
 */
export const EditionOrder = z.enum(['random', 'sequential']);

/**
 * The fields of a reward, separately from the cross-field rules below.
 *
 * Exported because the PATCH path needs to know which keys are a client's to
 * send, and deriving that from the one schema is what keeps "what you may
 * create" and "what you may edit" from becoming two lists that drift (L16).
 */
export const RewardFields = z.object({
  name: z.string().min(1).max(120),
  pool: RewardPool,
  rarity: RewardRarity,
  supply: RewardSupply,
  editions: EditionOrder.default('random'),
  active: z.boolean().default(true),
});

export const RewardInput = RewardFields
  .refine(
    (r) => r.supply.axis !== 'per_item_tier' || r.rarity.mode !== 'none',
    {
      // A per-tier print run with no tiers has nothing to key its counters on.
      message: 'A per-tier print run needs a rarity mode. Roll them, or assign one per item.',
      path: ['supply'],
    }
  )
  .refine(
    (r) => !(r.supply.axis === 'per_item_tier' && r.rarity.mode === 'rolled' &&
             Object.keys(r.rarity.weights).length > 0),
    {
      // Two answers to one question. stefancoat's VinylRarityConfig carried
      // both `tierWeights` and `tierCounts` with nothing preventing both being
      // set, and picked a mode by field presence.
      message:
        'A per-tier print run already sets the odds — 100 commons to 5 legendaries IS the ' +
        'distribution. Remove the weights, or choose a different supply axis.',
      path: ['rarity', 'weights'],
    }
  )
  .refine(
    (r) => r.supply.axis === 'per_item_tier' || r.rarity.mode !== 'rolled' ||
           Object.keys(r.rarity.weights).length > 0,
    {
      message: 'Rolled rarity needs a weight for at least one tier.',
      path: ['rarity', 'weights'],
    }
  )
  .refine(
    (r) => r.supply.axis !== 'per_item_tier' ||
           Object.values(r.supply.limits).some((n) => n > 0),
    {
      message: 'Every tier is set to zero, so this reward could never mint anything.',
      path: ['supply', 'limits'],
    }
  );

/**
 * What this reward has minted and what is left, summed across every counter
 * it owns.
 *
 * DERIVED and server-owned. There is no single `limit` to publish any more:
 * a per-item run holds one counter per item and a print sheet holds one per
 * (item, tier), created lazily on first mint. Copying a total onto the reward
 * would be a second home for a number only the counters can know — the shape
 * of the bug where the API went on reporting `issued: 0` after the hundredth
 * copy.
 *
 * It is also the number an author most needs to see. Their editor's warning,
 * worth repeating wherever this renders: *"'Common 100' across a 14-card pool
 * means 1,400 commons, not 100 — getting that backwards is a
 * two-orders-of-magnitude mistake in how much of a season exists."*
 */
export const RewardStock = z.object({
  /** How many items the pool resolves to right now. A tag pool is live. */
  pool_size: z.number().int().min(0),
  /** Copies minted so far. */
  minted: z.number().int().min(0),
  /** Copies this reward could ever mint. null = unlimited. */
  capacity: z.number().int().min(0).nullable(),
  /** Copies still available. null = unlimited. */
  remaining: z.number().int().min(0).nullable(),
});

export const Reward = z.object({
  id: z.string(),
  channel_id: z.string(),
  name: z.string(),
  pool: RewardPool,
  rarity: RewardRarity,
  supply: RewardSupply,
  editions: EditionOrder,
  active: z.boolean(),
  stock: RewardStock,
});

export type RewardPool = z.infer<typeof RewardPool>;
export type RewardRarity = z.infer<typeof RewardRarity>;
export type RewardSupply = z.infer<typeof RewardSupply>;
export type RewardStock = z.infer<typeof RewardStock>;
export type RewardInput = z.infer<typeof RewardInput>;
export type Reward = z.infer<typeof Reward>;
